import React from 'react';
import { Annotations, Dialogues, Discussions, Inspectors, PersistZone, ProposeZone } from '@pitaman71/omniglot-live-data';
import { makeStyles, Breadcrumbs, Box, Link } from '@material-ui/core';

import * as Debug from '../Debug';
import * as WebStorage from '../WebStorage';

const __moduleName__ = 'omniglot-live-react.MaterialUi.ViewDiscussion';

export const ReactContext = React.createContext<undefined|Discussions.Interface>(undefined);

export function Provide(props: React.PropsWithChildren<{
    root: Dialogues.Instance<any>
}>) {
    const [ local,  ] = React.useState(() => {
        const persist = new WebStorage.Journal('private', localStorage); 
        persist.watchWindow(window);
        return new PersistZone.PersistZone(persist);
    })
    const [ page, setPage ] = React.useState<undefined|Discussions.Entry<any>>(undefined);
    const [ stack, setStack ] = React.useState<Discussions.Entry<any>[]>([]);

    return (
        <Debug.Boundary name={`${__moduleName__}.Provide ${props.root}`}>
            <ReactContext.Provider value={ new class implements Discussions.Interface {
                zone() { return stack.length > 0 ? stack[stack.length - 1].zone : page ? page.zone : local }
                page() { return new class {
                    open<DialogueTypeParams extends Dialogues.TypeParams>(
                        dialogue: Dialogues.Instance<DialogueTypeParams>
                    ): void {
                        const zone_ = new ProposeZone.ProposeZone('ViewDiscussion.Provide.page', local);
                        setPage({ dialogue, zone: zone_ });
                    }
                    get(): undefined|Discussions.Entry<any> {
                        return page;
                    }
                }}
                top() {
                    if(stack.length > 0) {
                        return stack[stack.length - 1];
                    }
                    return page
                }
                panels() { 
                    const discussion = this;
                    return new class {
                        stack(): Discussions.Entry<any>[] { return [ ...stack ] }
                        open<DialogueTypeParams extends Dialogues.TypeParams>(
                            dialogue: Dialogues.Instance<DialogueTypeParams>
                        ): void { 
                            const zone_ = new ProposeZone.ProposeZone('ViewDiscussion.Provide.panels', discussion.zone())
                            setStack(_stack => [ ..._stack, { dialogue, zone: zone_ } ]) 
                        }
                        close(): void { 
                            setStack(_stack => _stack.slice(0, _stack.length - 1))
                        }
                    }()
                }
            }()}>
                {props.children}
            </ReactContext.Provider>
        </Debug.Boundary>
    );
}

export function AsBreadcrumbs(props: {
    makeAnnotations: Annotations.Maker<JSX.Element>,
    makeInspectors: Inspectors.Maker<JSX.Element>
}) {
    const useStyles = makeStyles((theme) => ({
        root: {
          '& .MuiTextField-root': {
            marginTop: theme.spacing(2)
          },
        },
        link: {
            display: 'flex',
        },
        icon: {
            marginRight: theme.spacing(0.5),
            width: 20,
            height: 20,
        },
        overlay: {
            margin: theme.spacing(1),
            position: 'relative',
        },
    }));
    
    const classes = useStyles();
    const viewConfiguration = React.useContext(ReactContext);

    const panelStack = viewConfiguration === undefined ? [] : viewConfiguration.panels().stack();
    const page = viewConfiguration?.page().get();
    const panel = panelStack.length === 0 ? undefined : panelStack[panelStack.length - 1];

    return (
        <Debug.Boundary name={`${__moduleName__}.AsBreadcrumbs`}>
            <Breadcrumbs>
                {
                    panelStack.map(panel => {
                        const annotations = props.makeAnnotations.dialogue(panel.dialogue.descriptor);
                        return (
                            <Link className={classes.link}>
                                {annotations.icon}
                                <div style={{ marginTop: '0.07em', marginLeft: '0.3em' }}>{annotations.label}</div>
                            </Link>
                        );
                    })
                }
            </Breadcrumbs>
        </Debug.Boundary>
    );
}

export function AsTopPanel(props: {
    makeAnnotations: Annotations.Maker<JSX.Element>,
    makeInspectors: Inspectors.Maker<JSX.Element>
}) {
    const useStyles = makeStyles((theme) => ({
        root: {
            height: '100%',
            '& .MuiTextField-root': {
                marginTop: theme.spacing(2)
            }
        }
    }));
    
    const classes = useStyles();
    const viewConfiguration = React.useContext(ReactContext);

    const panelStack = viewConfiguration === undefined ? [] : viewConfiguration.panels().stack();
    const page = viewConfiguration?.page().get();
    const panel = panelStack.length === 0 ? undefined : panelStack[panelStack.length - 1];

    return (
        <Debug.Boundary name={`${__moduleName__}.AsTopPanel`}>
            <form className={classes.root} noValidate>
                { panel === undefined && page && props.makeInspectors.panel(page.dialogue, page.zone) }
                { panel !== undefined && props.makeInspectors.panel(panel.dialogue, panel.zone) }
            </form>
        </Debug.Boundary>
    );
}
